// frontend/src/components/player/FancyInvitationPreview.tsx

import React, { useEffect, useRef, useState } from 'react';
import { TemplateType } from '../../types/Template';
import './FancyInvitationPreview.css';

interface Props {
  template: TemplateType;
  data: Record<string, any>;
  onError?: (error: Error) => void;
}

const FancyInvitationPreview: React.FC<Props> = ({ template, data, onError }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [previewReady, setPreviewReady] = useState(false);

  /* ──────────────────────────────────────────────────────────────
     ❶  Build & mount the sandboxed iframe, relay READY / ERROR
     ───────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!template.overlayBundle?.css || !template.overlayBundle?.js) {
      console.error('Missing overlay bundle resources for fancy preview');
      onError?.(new Error('Missing template resources'));
      return;
    }

    const cssUrl = template.overlayBundle.css;
    const jsUrl  = template.overlayBundle.js;

    const previewHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport"
                content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }

            html, body {
              width: 100%;
              height: 100%;
              overflow: hidden;
              background: #000;
            }

            /* Ensure the invitation fills the space */
            .invite-preview,
            .video-container {
              position: relative !important;
              width: 100% !important;
              height: 100% !important;
              max-width: 100% !important;
              max-height: 100% !important;
            }

            .video-container { aspect-ratio: 9/16 !important; margin: 0 auto !important; }

            /* Position overlays */
            .rsvp-wrapper {
              position: fixed !important;
              bottom: 80px !important;
              left: 0 !important;
              right: 0 !important;
              width: 100% !important;
              z-index: 100 !important;
            }

            .blast-button {
              position: fixed !important;
              bottom: 20px !important;
              left: 50% !important;
              transform: translateX(-50%) !important;
              z-index: 100 !important;
            }

            /* Template mute button (if any) */
            button[onclick*="bgMusic"] {
              position: fixed !important;
              top: 10px !important;
              right: 10px !important;
              z-index: 1000 !important;
            }

            /* Let the template control audio */
            audio, video { }
          </style>

          <!-- Template-level styles -->
          <link rel="stylesheet" href="${cssUrl}">
        </head>

        <body>
          <div id="fancy-root"></div>

          <script>
            /* Hand off the invite data to the template bundle */
            window.__INVITE_DATA__ = ${JSON.stringify({
              ...data,
              previewMode: true,
              muted: false,
              autoplay: true
            })};

            /* Block pop-ups inside the sandbox */
            window.open = function () { return null; };

            /* Load the template player JS */
            const script = document.createElement('script');
            script.src = '${jsUrl}';

            script.onload = function () {
              setTimeout(function () {
                if (window.initInvite) {
                  try {
                    const root = document.getElementById('fancy-root');
                    window.initInvite(root, window.__INVITE_DATA__, { mode: 'preview' });

                    /* Autoplay background music if present */
                    if (window.bgMusic) {
                      try {
                        window.bgMusic.muted  = false;
                        window.bgMusic.volume = 1;
                        window.bgMusic.play().catch(function () {});
                      } catch (_) { /* ignore */ }
                    }

                    window.parent.postMessage({ type: 'PREVIEW_READY' }, '*');
                  } catch (err) {
                    window.parent.postMessage({
                      type:  'PREVIEW_ERROR',
                      error: err?.message || String(err)
                    }, '*');
                  }
                }
              }, 500);
            };

            script.onerror = function () {
              window.parent.postMessage({
                type:  'PREVIEW_ERROR',
                error: 'Failed to load template script'
              }, '*');
            };

            document.body.appendChild(script);
          </script>
        </body>
      </html>
    `;

    /* Mount the iframe */
    const iframe = document.createElement('iframe');
    iframe.allow        = 'autoplay';
    iframe.style.border = 'none';
    iframe.style.width  = '100%';
    iframe.style.height = '100%';
    iframe.srcdoc       = previewHtml.trim();

    iframeRef.current?.remove();
    iframeRef.current = iframe;
    containerRef.current?.appendChild(iframe);

    /* Relay READY / ERROR from the iframe */
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'PREVIEW_READY') {
        setIsLoading(false);
        setPreviewReady(true);
      } else if (event.data.type === 'PREVIEW_ERROR') {
        setIsLoading(false);
        onError?.(new Error(event.data.error));
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      iframe.remove();
    };
  }, [template, data, onError]);

  /* ──────────────────────────────────────────────────────────────
     ❷  NEW ► pause audio when parent broadcasts MUTE_PREVIEW_AUDIO
     ───────────────────────────────────────────────────────────── */
  useEffect(() => {
    const handleMute = (e: MessageEvent) => {
      if (e.data?.type !== 'MUTE_PREVIEW_AUDIO') return;

      try {
        const innerWin = iframeRef.current?.contentWindow as any;
        if (!innerWin) return;

        /* Pause template-level bgMusic, if present */
        if (innerWin.bgMusic && !innerWin.bgMusic.paused) {
          innerWin.bgMusic.pause();
        }

        /* Pause any <audio> tags inside the iframe */
        innerWin.document
          ?.querySelectorAll?.('audio')
          ?.forEach((a: any) => (a as HTMLAudioElement).pause());
      } catch (_) { /* ignore */ }
    };

    window.addEventListener('message', handleMute);
    return () => window.removeEventListener('message', handleMute);
  }, []);

  /* ──────────────────────────────────────────────────────────────
     Render
     ───────────────────────────────────────────────────────────── */
  return (
    <div ref={containerRef} className="fancy-invitation-preview-wrapper">
      {isLoading && (
        <div className="fancy-preview-loading">
          <div className="fancy-spinner" />
          <p>Loading preview...</p>
        </div>
      )}

      <iframe
        ref={iframeRef}
        className={`fancy-preview-iframe ${previewReady ? 'ready' : ''}`}
        title="Fancy Invitation Preview"
        allow="autoplay"
      />

      {previewReady && (
        <div className="fancy-preview-overlay">
          <div className="preview-badge">PREVIEW</div>
        </div>
      )}
    </div>
  );
};

export default FancyInvitationPreview;